import React from 'react';
import PropTypes from 'prop-types';

import { COLORS } from 'styleguide';

import {
  MODERATOR,
  BILLER,
  JORNALIST,
  EDITOR,
  CORRECTOR,
  SUPERVISOR,
  ROLES_OBJECT,
} from 'utils/roles';

import Avatar from 'components/Avatar';
import AuditChangesItem from 'components/AuditChangesItem';
import DashedButton from 'components/DashedButton';

import SimpleList from 'styled/SimpleList';

import { ReactComponent as Chevron } from 'icons/Chevron.svg';
import { ReactComponent as Wifi } from 'icons/Wifi.svg';
import { ReactComponent as Time } from 'icons/Time.svg';
import { ReactComponent as Info } from 'icons/Info.svg';

import PostInfo from './PostInfo';
import UserBlock from './UserBlock';
import AuditHeading from './AuditHeading';
import ButtonRequestShow from './ButtonRequestShow';
import Block from './Block';
import Header from './Header';
import AuditRowWrapper from './AuditRowWrapper';

const CHANGES_COUNT = 3;

const EVENTS = {
  create: 'Создана',
  update: 'Обновлена',
  destroy: 'Удалена',
};

const TYPES = {
  Post: 'публикации',
  ItemSnooze: 'изменение снузов',
  'Media::MediaFile': 'изменение обложки',
  TaskMaterial: 'материала',
};

export class AuditRow extends React.PureComponent {
  state = {
    isInfoOpen: false,
    isAllChangesOpen: false,
  }

  get date() {
    return new Date(this.props.createdAt * 1000).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    });
  }

  get employeeRoles() {
    return this.props.employeeRoles ? this.props.employeeRoles.map(role => ROLES_OBJECT[role]) : [];
  }

  get filteredChanges() {
    return Object.keys(this.props.objectChanges)
      .filter(change => !['reactionCounters', 'updatedAt'].includes(change));
  }

  handleRequestBlockToggle = () => {
    this.setState(prevState => ({ isInfoOpen: !prevState.isInfoOpen }));
  }

  handleAllChangesOpenClick = () => {
    this.setState({ isAllChangesOpen: true });
  }

  handleAllChangesCloseClick = () => {
    this.setState({ isAllChangesOpen: false });
  }

  render() {
    return (
      <AuditRowWrapper>
        <Header>
          <UserBlock>
            <Avatar
              name={this.props.employeeFullname}
              size="56px"
              hasBorder
              url={this.props.employeeAvatar}
            />

            <div style={{ flexGrow: '1', margin: '8px 16px' }}>
              <AuditHeading>{this.props.employeeFullname}</AuditHeading>

              {this.employeeRoles.join(', ')}
            </div>

            <ButtonRequestShow
              onClick={this.handleRequestBlockToggle}
              type="button"
              style={{ width: '220px', margin: '8px 0' }}
            >
              Подробная информация

              <Chevron
                style={{
                  transform: this.state.isInfoOpen ? 'rotate(180deg)' : 'rotate(0)',
                  marginLeft: '5px',
                }}
              />
            </ButtonRequestShow>
          </UserBlock>

          {this.state.isInfoOpen && (
            <div style={{ paddingTop: '16px', borderTop: `1px solid ${COLORS.line}` }}>
              <Block>
                <span style={{ marginRight: '8px' }}><Info /></span>

                {this.props.employeeUserAgent}
              </Block>

              <div style={{ marginTop: '16px' }}>
                <span style={{ marginRight: '8px' }}><Wifi /></span>

                {this.props.employeeIp}
              </div>
            </div>
          )}
        </Header>

        <div style={{ padding: '25px 15px', backgroundColor: `${COLORS.line}` }}>
          <PostInfo>
            <div>
              <AuditHeading>Состояние {TYPES[this.props.itemType]} #{this.props.itemId}</AuditHeading>

              <span style={{ marginRight: '5px' }}>Действие над моделью:</span>

              {EVENTS[this.props.event]}
            </div>

            <Block>
              <span style={{ marginRight: '8px' }}><Time /></span>

              {this.date}
            </Block>
          </PostInfo>

          <SimpleList style={{ paddingTop: '15px' }}>
            {this.filteredChanges.slice(0, CHANGES_COUNT).map(item => (
              <AuditChangesItem
                key={`${this.props.id}-${item}`}
                item={this.props.objectChanges[item]}
                actionType={item}
              />
            ))}

            {this.filteredChanges.length > CHANGES_COUNT && !this.state.isAllChangesOpen && (
              <li style={{ padding: '10px 0 20px' }}>
                <DashedButton
                  type="button"
                  onClick={this.handleAllChangesOpenClick}
                  aria-label="Показать все изменения"
                >
                  Показать все изменения
                </DashedButton>
              </li>
            )}

            {this.filteredChanges.length > CHANGES_COUNT
              && this.state.isAllChangesOpen
              && this.filteredChanges.slice(CHANGES_COUNT).map(item => (
                <AuditChangesItem
                  key={`${this.props.id}-${item}`}
                  item={this.props.objectChanges[item]}
                  actionType={item}
                />
            ))}

            {this.filteredChanges.length > CHANGES_COUNT && this.state.isAllChangesOpen && (
              <li style={{ padding: '10px 0 20px' }}>
                <DashedButton
                  type="button"
                  onClick={this.handleAllChangesCloseClick}
                  aria-label="Скрыть все изменения"
                >
                  Скрыть все изменения
                </DashedButton>
              </li>
            )}
          </SimpleList>
        </div>
      </AuditRowWrapper>
    );
  }
}

AuditRow.propTypes = {
  id: PropTypes.string.isRequired,
  employeeFullname: PropTypes.string,
  employeeAvatar: PropTypes.string,
  employeeRoles: PropTypes.arrayOf(
    PropTypes.oneOf([EDITOR, SUPERVISOR, MODERATOR, CORRECTOR, BILLER, JORNALIST])
  ),
  event: PropTypes.oneOf(['create', 'update', 'destroy']).isRequired,
  itemId: PropTypes.string.isRequired,
  employeeIp: PropTypes.string,
  employeeUserAgent: PropTypes.string,
  createdAt: PropTypes.number,
  objectChanges: PropTypes.shape({}),
  itemType: PropTypes.oneOf(['Post', 'ItemSnooze', 'Media::MediaFile', 'TaskMaterial']),
};

AuditRow.defaultProps = {
  employeeFullname: '-',
  employeeAvatar: '',
  employeeRoles: [],
  employeeIp: '',
  employeeUserAgent: '',
  createdAt: undefined,
  objectChanges: {},
  itemType: 'Post',
};

export default AuditRow;
